import {Box, Button, ButtonBase, IconButton, InputAdornment, Popover, Stack, TextField, Typography, useMediaQuery, useTheme} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import {useVirtualizer} from "@tanstack/react-virtual";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {GERMAN_SECTOR_ICON_TRANSLATIONS} from "./germanSectorIconTranslations";
import {getSectorIconOption, SECTOR_ICON_OPTIONS} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";

const ICON_OPTION_HEIGHT = 58;
const ICON_GRID_GAP = 6;
const ICON_ROW_HEIGHT = ICON_OPTION_HEIGHT + ICON_GRID_GAP;
const ICON_GRID_HEIGHT = 382;

const normalizeIconSearch = (value: string) => value
    .trim()
    .toLocaleLowerCase("de")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss");

interface SectorIconPickerProps {
    value?: string;
    defaultIconKey?: string;
    onChange: (value?: string) => void;
}

const SectorIconPicker = ({value, defaultIconKey, onChange}: SectorIconPickerProps) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
    const isGerman = (i18n.resolvedLanguage || i18n.language)
        .toLowerCase()
        .split(/[-_]/)[0] === "de";
    const columnCount = isSmUp ? 6 : 4;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [search, setSearch] = useState("");
    const iconGridRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const effectiveValue = value || defaultIconKey || "default";
    const selected = getSectorIconOption(effectiveValue) ?? getSectorIconOption("default");
    const SelectedIcon = selected?.Icon ?? EventOutlinedIcon;
    const selectedOptionLabel = selected && isGerman
        ? GERMAN_SECTOR_ICON_TRANSLATIONS[selected.key]?.label ?? selected.label
        : selected?.label;
    const selectedLabel = value
        ? selectedOptionLabel ?? t("Default")
        : defaultIconKey && selected
            ? t("Default ({{icon}})", {icon: selectedOptionLabel})
            : t("Default");
    const open = Boolean(anchorEl);

    const filteredOptions = useMemo(() => {
        const needle = normalizeIconSearch(search);
        if (!needle) return SECTOR_ICON_OPTIONS;

        return SECTOR_ICON_OPTIONS.filter(option => {
            const germanTranslation = GERMAN_SECTOR_ICON_TRANSLATIONS[option.key];
            const searchableText = [
                option.key,
                option.label,
                option.search,
                germanTranslation?.label,
                germanTranslation?.aliases,
            ].filter(Boolean).join(" ");

            return normalizeIconSearch(searchableText).includes(needle);
        });
    }, [search]);
    const rowCount = Math.ceil(filteredOptions.length / columnCount);
    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => iconGridRef.current,
        estimateSize: () => ICON_ROW_HEIGHT,
        overscan: 2,
        initialRect: {width: 432, height: ICON_GRID_HEIGHT},
    });

    useEffect(() => {
        if (!open) return;

        iconGridRef.current?.scrollTo({top: 0});
        rowVirtualizer.measure();
    }, [columnCount, open, rowVirtualizer, search]);

    const closePicker = () => {
        setAnchorEl(null);
        setSearch("");
    }

    const pickIcon = (key?: string) => {
        onChange(key === "default" ? undefined : key);
        closePicker();
    }

    return (
        <>
            <Button
                id="sector-sidebar-icon-picker"
                variant="outlined"
                fullWidth
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={open ? "sector-sidebar-icon-popover" : undefined}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    height: 40,
                    justifyContent: "space-between",
                    color: "text.primary",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    textTransform: "none",
                }}
            >
                <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                    <SelectedIcon fontSize="small"/>
                    <Typography variant="body2" noWrap>
                        {selectedLabel}
                    </Typography>
                </Stack>
            </Button>

            <Popover
                id="sector-sidebar-icon-popover"
                open={open}
                anchorEl={anchorEl}
                onClose={closePicker}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                marginThreshold={12}
                TransitionProps={{
                    onEntered: () => searchInputRef.current?.focus(),
                }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        maxWidth: "calc(100vw - 24px)",
                    },
                }}
            >
                <Box sx={{width: 456, maxWidth: "calc(100vw - 48px)"}}>
                    <Box sx={{px: 1.5, pt: 1.5}}>
                        <TextField
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t("Search icons...")}
                            size="small"
                            fullWidth
                            autoFocus
                            inputRef={searchInputRef}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small"/>
                                    </InputAdornment>
                                ),
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={t("Clear search")}
                                            edge="end"
                                            size="small"
                                            onClick={() => setSearch("")}
                                        >
                                            <ClearIcon fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />

                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            {t("{{count}} matching icons", {count: filteredOptions.length})}
                        </Typography>
                    </Box>

                    <Box
                        ref={iconGridRef}
                        sx={{
                            height: `min(${ICON_GRID_HEIGHT}px, calc(100vh - 168px))`,
                            overflowY: "auto",
                            mt: 1,
                            mb: 1.5,
                            pl: 1.5,
                            pr: 2,
                        }}
                    >
                        {filteredOptions.length ? (
                            <Box
                                sx={{
                                    height: rowVirtualizer.getTotalSize(),
                                    position: "relative",
                                    width: "100%",
                                }}
                            >
                                {rowVirtualizer.getVirtualItems().map(virtualRow => (
                                    <Box
                                        key={virtualRow.key}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                                            gap: `${ICON_GRID_GAP}px`,
                                            height: ICON_OPTION_HEIGHT,
                                            left: 0,
                                            position: "absolute",
                                            top: 0,
                                            transform: `translateY(${virtualRow.start}px)`,
                                            width: "100%",
                                        }}
                                    >
                                        {filteredOptions
                                            .slice(
                                                virtualRow.index * columnCount,
                                                (virtualRow.index + 1) * columnCount,
                                            )
                                            .map(option => {
                                                const isDefaultOption = option.key === "default";
                                                const Icon = isDefaultOption ? SelectedIcon : option.Icon;
                                                const optionLabel = isDefaultOption
                                                    ? selectedLabel
                                                    : isGerman
                                                        ? GERMAN_SECTOR_ICON_TRANSLATIONS[option.key]?.label ?? option.label
                                                        : option.label;
                                                const isSelected = isDefaultOption ? !value : option.key === value;

                                                return (
                                                    <ButtonBase
                                                        key={option.key}
                                                        onClick={() => pickIcon(option.key)}
                                                        sx={{
                                                            height: ICON_OPTION_HEIGHT,
                                                            borderRadius: 1,
                                                            border: "1px solid",
                                                            borderColor: isSelected ? "primary.main" : "transparent",
                                                            bgcolor: isSelected ? "action.selected" : "transparent",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            justifyContent: "center",
                                                            gap: 0.5,
                                                            px: 0.5,
                                                            "&:hover": {
                                                                bgcolor: "action.hover",
                                                            },
                                                        }}
                                                    >
                                                        <Icon fontSize="small"/>
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                width: "100%",
                                                                textAlign: "center",
                                                                fontSize: 10,
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {optionLabel}
                                                        </Typography>
                                                    </ButtonBase>
                                                );
                                            })}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    height: "100%",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {t("No icons found")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Popover>
        </>
    );
}

export default SectorIconPicker;
